import { gatewayFetch } from './gateway.js';

const TOKEN_STORAGE_KEY = 'access_token';
const SESSION_STORAGE_KEY = 'mm_quick_session';

/**
 * Ensures an active, unexpired app session exists in localStorage.
 * If missing or expired, it automatically authenticates headlessly using Vite environment variables.
 */
export async function ensureAppSession() {
  const existingToken = localStorage.getItem(TOKEN_STORAGE_KEY);

  if (existingToken) {
    try {
      // Decode JWT payload to verify token hasn't expired
      const payloadBase64 = existingToken.split('.')[1];
      const payload = JSON.parse(atob(payloadBase64));
      
      // If valid and not expiring in the next 60 seconds, reuse it
      if (payload.exp && payload.exp * 1000 > Date.now() + 60000) {
        return existingToken;
      }
    } catch {
      // If parsing fails, fall through to re-login
      logout();
    }
  }

  return await loginAppSession();
}

/**
 * Authenticates the Quick Bookings service account against public_gateway.
 */
export async function loginAppSession(
  email = import.meta.env.VITE_APP_AUTH_EMAIL || 'quickbookings@majormaker.org',
  pin = import.meta.env.VITE_APP_AUTH_PIN || 'QuickApp2026!'
) {
  const cleanEmail = String(email).trim().toLowerCase();
  const cleanPin = String(pin).trim();

  if (!cleanEmail || !cleanPin) {
    throw new Error('Quick Bookings app credentials missing from environment.');
  }

  const endpoint = '/api/v1/public/auth/login';

  try {
    const data = await gatewayFetch(endpoint, {
      method: 'POST',
      body: JSON.stringify({
        email: cleanEmail,
        password: cleanPin,
        filter_key: 'QuickBookings'
      })
    });

    if (!data || !data.access_token) {
      throw new Error(data?.detail || 'Failed to initialize app session.');
    }

    const sessionProfile = {
      appId: 'quick-bookings-app',
      email: cleanEmail,
      filterKey: data.filter_key || 'QuickBookings',
      token: data.access_token
    };

    localStorage.setItem(TOKEN_STORAGE_KEY, data.access_token);
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionProfile));

    return data.access_token;
  } catch (error) {
    console.error('Headless App Auth Error:', error);
    throw new Error(error.message || 'Quick Bookings session initialization failed.');
  }
}

export function isAuthenticated() {
  return !!localStorage.getItem(TOKEN_STORAGE_KEY);
}

export function logout() {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(SESSION_STORAGE_KEY);
}

// Backward compatibility alias for main.js imports
export const login = loginAppSession;